import { giphyDarkGrey, giphyIndigo, giphyRed, giphyYellow } from '@giphy/colors'
import { ReactNode, SyntheticEvent, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { BORDER_RADIUS_MEDIUM } from './../../constants'

enum DragPosition {
    Left = 'left',
    Right = 'right',
    Middle = 'middle',
}

const Container = styled.div`
    background: ${giphyDarkGrey};
    border-radius: ${BORDER_RADIUS_MEDIUM}px;
    min-height: 36px;
    position: relative;
    user-select: none;
`

const Wrapper = styled.div`
    border-radius: inherit;
    box-sizing: border-box;
    height: 100%;
    left: 0;
    overflow: hidden;
    padding: 0 16px;
    position: absolute;
    top: 0;
    width: 100%;
`

const Dim = styled.div`
    background: rgba(0, 0, 0, 0.7);
    bottom: 0;
    left: 16px;
    position: absolute;
    right: 16px;
    top: 0;
`

const Dragger = styled.div`
    border-radius: inherit;
    cursor: grab;
    height: 100%;
    position: relative;
    width: 100%;

    &:active {
        cursor: grabbing;
    }
`

const Handle = styled.div`
    align-items: center;
    border-radius: inherit;
    display: flex;
    flex-shrink: 0;
    height: 100%;
    justify-content: center;
    position: absolute;
    top: 0;
    width: 16px;

    &:after {
        content: '';
        background: #000;
        border-radius: 2px;
        height: 50%;
        width: 4px;
    }

    &[data-drag-position='${DragPosition.Left}'] {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        left: -16px;
    }

    &[data-drag-position='${DragPosition.Right}'] {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        right: -16px;
    }
`

const Middle = styled.div`
    border: 6px solid;
    border-left: 0;
    border-right: 0;
    height: 100%;
    width: 100%;
`

export type CreationToolsRangeSliderProps = {
    className?: string
    children?: ReactNode
    start?: number
    end?: number
    isInvalid?: boolean
    customColor?: string
    onUpdate: (start: number, end: number) => void
    childrenAtMiddle?: boolean
    onDragStart?: () => void
    onDragEnd?: () => void
}

const CreationToolsRangeSlider = ({
    className,
    children,
    start = 0,
    end = 1,
    isInvalid = false,
    customColor,
    onUpdate,
    childrenAtMiddle,
    onDragStart,
    onDragEnd,
}: CreationToolsRangeSliderProps) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const [startValue, setStartValue] = useState<number>(start)
    const [endValue, setEndValue] = useState<number>(end)
    const isOverlayTrimmer = !!children // indicates whether this trimmer is showing content behind it or not
    const color = isInvalid ? giphyRed : isOverlayTrimmer ? giphyYellow : giphyIndigo
    const handleCss = { background: customColor || color }
    const middleCss = isOverlayTrimmer
        ? { borderColor: customColor || color }
        : { background: customColor || color, border: 0 }

    useEffect(() => {
        setStartValue(start)
        setEndValue(end)
    }, [start, end])

    const handleOnDragStart = (e: SyntheticEvent<HTMLDivElement, MouseEvent>) => {
        const { dragPosition } = (e.target as HTMLDivElement).dataset
        onDragStart?.()

        if (!containerRef.current || !dragPosition) return

        // set initial values for comparing
        const { width } = containerRef.current.getBoundingClientRect()
        const startX = e.nativeEvent.pageX
        const baseStartValue = startValue
        const baseEndValue = endValue

        document.onmousemove = (e: DragEvent) => {
            // get the distance moved
            const diffX = e.pageX - startX
            const diffProgress = diffX / width
            let newStart = baseStartValue
            let newEnd = baseEndValue

            switch (dragPosition) {
                case DragPosition.Left:
                    newStart = Math.max(0, Math.min(baseStartValue + diffProgress, baseEndValue))
                    break
                case DragPosition.Right:
                    newEnd = Math.min(1, Math.max(baseStartValue, baseEndValue + diffProgress))
                    break
                case DragPosition.Middle:
                    const range = baseEndValue - baseStartValue
                    newStart = Math.max(0, Math.min(baseStartValue + diffProgress, 1 - range))
                    newEnd = newStart + range
                    break
            }

            setStartValue(newStart)
            setEndValue(newEnd)
            onUpdate(newStart, newEnd)
        }

        document.onmouseup = () => {
            onDragEnd?.()
            document.onmousemove = null
            document.onmouseup = null
        }
    }

    return (
        <Container className={className} ref={containerRef}>
            {!childrenAtMiddle && children}
            <Wrapper>
                {isOverlayTrimmer && (
                    <>
                        <Dim style={{ transform: `translate3d(${(startValue - 1) * 100}%, 0, 0)` }} />
                        <Dim style={{ transform: `translate3d(${endValue * 100}%, 0, 0)` }} />
                    </>
                )}
                <Dragger
                    onMouseDown={handleOnDragStart}
                    style={{
                        left: `${(startValue / 1) * 100}%`,
                        width: `${(endValue - startValue) * 100}%`,
                    }}
                >
                    <Handle style={handleCss} data-drag-position={DragPosition.Left} />
                    <Middle style={middleCss} data-drag-position={DragPosition.Middle}>
                        {childrenAtMiddle && children}
                    </Middle>
                    <Handle style={handleCss} data-drag-position={DragPosition.Right} />
                </Dragger>
            </Wrapper>
        </Container>
    )
}

export default CreationToolsRangeSlider
